import { MagnifyingGlass, Warning } from '@phosphor-icons/react';

/** Skeleton grid shown on first load only. */
export function ResultsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-[1.5rem] border border-sand bg-white">
          <div className="aspect-[4/3] animate-pulse bg-cream-deep" />
          <div className="space-y-2 p-5">
            <div className="h-4 w-2/3 animate-pulse rounded bg-cream-deep" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-cream-deep" />
            <div className="mt-4 h-4 w-1/3 animate-pulse rounded bg-cream-deep" />
          </div>
        </div>
      ))}
      <span className="sr-only">Loading hotels…</span>
    </div>
  );
}

interface EmptyStateProps {
  hasFilters: boolean;
  place: string;
  onClearFilters: () => void;
}

export function EmptyState({ hasFilters, place, onClearFilters }: EmptyStateProps) {
  return (
    <div className="rounded-[1.5rem] border border-sand bg-white p-12 text-center">
      <MagnifyingGlass className="mx-auto h-12 w-12 text-clay" aria-hidden="true" />
      {hasFilters ? (
        <>
          <h2 className="mt-4 font-serif text-2xl font-bold text-ink">No hotels match all filters</h2>
          <p className="mt-2 text-ink-muted">Try removing a filter or widening your price range.</p>
          <button
            type="button"
            onClick={onClearFilters}
            className="mt-6 rounded-full bg-clay px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-clay-dark"
          >
            Clear filters
          </button>
        </>
      ) : (
        <>
          <h2 className="mt-4 font-serif text-2xl font-bold text-ink">
            No hotels found{place ? ` in ${place}` : ''}
          </h2>
          <p className="mt-2 text-ink-muted">Try a different destination or adjust your dates.</p>
        </>
      )}
    </div>
  );
}

export function ErrorState({ onRetry, throttled }: { onRetry: () => void; throttled: boolean }) {
  return (
    <div role="alert" className="rounded-[1.5rem] border border-sand bg-white p-12 text-center">
      <Warning className="mx-auto h-12 w-12 text-clay" aria-hidden="true" />
      <h2 className="mt-4 font-serif text-2xl font-bold text-ink">
        {throttled ? 'Too many searches' : 'Something went wrong loading hotels'}
      </h2>
      <p className="mt-2 text-ink-muted">
        {throttled ? "You're searching very fast — try again in a moment." : 'Please try again.'}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 rounded-full bg-clay px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-clay-dark"
      >
        Retry
      </button>
    </div>
  );
}
